import { useEffect } from 'react';
import { handleSelectPoint } from '../MapMarkers';
import { POINTS_CIRCLE_LAYER } from '../constants';
import { isMapClickAddPointEnabled, isPointSelectionEnabled } from '../../Services';

/**
 * Wire map click -> create location (Custom Path), and allow selecting existing points.
 * We append a location directly here so it works even if handleMapClick was tailored
 * for another mode.
 */
export const useMapInteractions = ({
  map,
  mapReady,
  sourcesReadyRef,
  isMountedRef,
  currentMode,
  locations,
  setLocations,
  mapPointSelectCallback,
  addPointType,
  defaultQuantity
}) => {
  // Background click to ADD a new point
  useEffect(() => {
    if (!map.current || !mapReady || !sourcesReadyRef.current || !isMountedRef.current) return;

    const addPointOnClick = (e) => {
      if (!isMapClickAddPointEnabled(currentMode)) return;
      if (!e?.lngLat) return;
      const { lng, lat } = e.lngLat;

      setLocations((prev) => {
        const type = (addPointType || 'supply').toLowerCase();
        const countOfType = prev.filter(p => p.tpType === type).length + 1;
        const id = `tp-${Date.now()}-${Math.round(lng*1e5)}-${Math.round(lat*1e5)}`;

        const newPoint = {
          id,
          latitude: lat,
          longitude: lng,
          tpType: type,                                     // 'supply' | 'demand'
          quantity: Number(defaultQuantity) || 0,           // default qty
          name: type === 'supply' ? `Supply ${countOfType}` : `Demand ${countOfType}`,
          number: prev.length + 1,                          // label on map if used
          type: 'WAYPOINT'                                  // for your style rules (optional)
        };
        return [...prev, newPoint];
      });
    };

    map.current.on('click', addPointOnClick);
    return () => { if (map.current) map.current.off('click', addPointOnClick); };
  }, [currentMode, mapReady, addPointType, defaultQuantity, setLocations]);

  // Click on existing markers (optional selection)
  useEffect(() => {
    if (!map.current || !mapReady || !sourcesReadyRef.current || !isMountedRef.current) return;

    const pointClickHandler = (e) => {
      handleSelectPoint(e, { currentMode, locations, onMapPointSelect: mapPointSelectCallback });
    };

    if (isPointSelectionEnabled(currentMode) && mapPointSelectCallback) {
      map.current.on('click', POINTS_CIRCLE_LAYER, pointClickHandler);
    }

    return () => {
      if (map.current) map.current.off('click', POINTS_CIRCLE_LAYER, pointClickHandler);
    };
  }, [currentMode, locations, mapPointSelectCallback, mapReady]);
};
